import { useOrderHistoryStore } from "@/store/orderHistory";

export function isActionableHttpUrl(url) {
  return typeof url === "string" && /^https?:\/\//i.test(url.trim());
}

function substitutePlaceholders(url, { email, subscriberKey }) {
  const safeReplace = (str, pattern, value) =>
    str.replace(pattern, () => value);
  return safeReplace(
    safeReplace(
      safeReplace(url, /%%emailaddress%%/gi, email),
      /%%subscriberkey%%/gi,
      subscriberKey,
    ),
    /%%emailaddr%%/gi,
    email,
  );
}

export function resolveFromHistory(history, field) {
  return (
    [...history]
      .reverse()
      .map((item) => item?.order?.[field])
      .find(Boolean) || ""
  );
}

export function linkRedirectsNeedSubscriberKey(
  redirects = window.productDetails?.linkRedirects || [],
) {
  return redirects.some((redirect) =>
    /%%subscriberkey%%/i.test(redirect.url || ""),
  );
}

export function needsSubscriberKeyFetch(history) {
  if (resolveFromHistory(history, "billingSubscriberKey")) {
    return false;
  }

  if (!resolveFromHistory(history, "billingEmail")) {
    return false;
  }

  return linkRedirectsNeedSubscriberKey();
}

function effectiveCrossSellStep(historyItem) {
  if (
    historyItem?.order?.orderType !== "crosssell" ||
    historyItem.step == null
  ) {
    return null;
  }
  return historyItem.action === "skip" && historyItem.step > 0
    ? historyItem.step - 1
    : historyItem.step;
}

function lastPurchasedRedirectsItem(history = []) {
  for (let index = history.length - 1; index >= 0; index -= 1) {
    const item = history[index];
    const orderType = item?.order?.orderType;
    if (!item || orderType === "upsell") {
      continue;
    }
    return item;
  }
  return null;
}

function cartChoiceIds(cart) {
  const ids = new Set();
  for (const item of cart?.items || []) {
    if (item?.choice?.id != null && item.choice.id !== "") {
      ids.add(String(item.choice.id));
    }
  }
  return ids;
}

function purchasedChoiceIdsFromHistory(history = [], lastItem, displayMode) {
  const ids = new Set();
  const carts = [];

  if (displayMode === "last") {
    if (lastItem?.cart) {
      carts.push(lastItem.cart);
    } else {
      for (const item of history) {
        const orderType = item?.order?.orderType;
        if (item?.cart && (!orderType || orderType === "initial")) {
          carts.push(item.cart);
        }
      }
    }
  } else {
    for (const item of history) {
      if (item?.cart) {
        carts.push(item.cart);
      }
    }
  }

  for (const cart of carts) {
    for (const id of cartChoiceIds(cart)) {
      ids.add(id);
    }
  }
  return ids;
}

function matchesChoiceScopedRedirect(redirect, purchasedChoiceIds) {
  if (redirect.choiceParentId == null || redirect.choiceParentId === "") {
    return true;
  }
  return purchasedChoiceIds.has(String(redirect.choiceParentId));
}

function matchesLastPurchasedRedirect(redirect, lastItem, purchasedChoiceIds) {
  if (!lastItem) {
    return false;
  }

  const orderType = lastItem.order?.orderType;
  if (orderType === "crosssell") {
    const step = effectiveCrossSellStep(lastItem);
    return step != null && Number(redirect.crossSellStep) === Number(step);
  }

  if (!orderType || orderType === "initial") {
    return (
      redirect.crossSellStep == null &&
      matchesChoiceScopedRedirect(redirect, purchasedChoiceIds)
    );
  }

  return false;
}

/**
 * Resolve program access links from productDetails.linkRedirects,
 * filtered by purchased cross-sell steps and, for initial offers, by Choice.
 */
export function resolveProgramLinks(options = {}) {
  const displayMode = options.displayMode || "all";
  if (displayMode === "none") {
    return [];
  }

  const redirects =
    options.redirects || window.productDetails?.linkRedirects || [];
  const store = useOrderHistoryStore();
  const history = options.history || store.activeOrderHistory || [];
  const email = resolveFromHistory(history, "billingEmail");
  const subscriberKey = resolveFromHistory(history, "billingSubscriberKey");
  const lastItem = lastPurchasedRedirectsItem(history);
  const purchasedChoiceIds = purchasedChoiceIdsFromHistory(
    history,
    lastItem,
    displayMode,
  );

  const purchasedCrossSellSteps = new Set();
  for (const item of history) {
    const step = effectiveCrossSellStep(item);
    if (step != null) {
      purchasedCrossSellSteps.add(step);
    }
  }

  const seen = new Set();
  return redirects
    .filter((r) => {
      if (displayMode === "last") {
        return matchesLastPurchasedRedirect(r, lastItem, purchasedChoiceIds);
      }
      if (r.crossSellStep != null) {
        return purchasedCrossSellSteps.has(Number(r.crossSellStep));
      }
      return matchesChoiceScopedRedirect(r, purchasedChoiceIds);
    })
    .filter((r) => {
      const rawUrl = r.url || "";
      const needsSubscriberKey = /%%subscriberkey%%/i.test(rawUrl);
      return !needsSubscriberKey || subscriberKey;
    })
    .map((r) => ({
      label: r.label || "",
      url: substitutePlaceholders(r.url || "", { email, subscriberKey }),
      order: r.order || 0,
    }))
    .filter((link) => isActionableHttpUrl(link.url))
    .filter((link) => {
      if (seen.has(link.url)) {
        return false;
      }
      seen.add(link.url);
      return true;
    })
    .sort((a, b) => a.order - b.order);
}
